import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit3, FiDollarSign, FiShoppingCart, FiTrendingUp, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { productosService } from '../../services';

const ProductoVentas = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [producto, setProducto] = useState(null);
  const [historialVentas, setHistorialVentas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProductoYVentas();
  }, [id]);

  const loadProductoYVentas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Cargar historial de ventas del producto
      const result = await productosService.getHistorialVentas(id, {
        skip: 0,
        limit: 50
      });

      if (result.success) {
        setProducto(result.data.product);
        setHistorialVentas(result.data.history || []);
        setEstadisticas(result.data.stats || {});
        setPagination(result.data.pagination || {});
      } else {
        setError('No se pudo cargar el historial de ventas');
      }
    } catch (error) {
      console.error('Error cargando ventas:', error);
      setError('Error de conexión al cargar la información');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return <LoadingGrid count={4} message="Cargando historial de ventas..." />;
  }

  if (!producto) {
    return (
      <div className="error-container">
        <h2>Producto no encontrado</h2>
        <button onClick={() => navigate('/system/productos')} className="btn btn--primary">
          Volver a Productos
        </button>
      </div>
    );
  }

  const productTabs = [
    { id: 'datos', label: 'Datos Producto', route: '' },
    { id: 'ventas', label: 'Ventas', route: '/ventas' },
    { id: 'inventario', label: 'Inventario', route: '/inventario' },
    { id: 'formulacion', label: 'Formulación', route: '/formulacion' }
  ];

  return (
    <div className="cliente-detalle-page">
      {/* Header */}
      <div className="form-header">
        <button
          className="btn btn--ghost"
          onClick={() => navigate('/system/productos')}
          type="button"
        >
          <FiArrowLeft /> Volver a Productos
        </button>

        <h1 className="form-title">
          VENTAS - {producto.name}
        </h1>

        <div className="form-actions">
          <button
            className="btn btn--secondary"
            onClick={loadProductoYVentas}
            type="button"
          >
            <FiRefreshCw /> Actualizar
          </button>
          <button
            className="btn btn--primary"
            onClick={() => navigate(`/system/productos/${id}/editar`)}
            type="button"
          >
            <FiEdit3 /> Editar Producto
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="cliente-detail-form">
        {error && (
          <div className="alert alert--danger">
            {error}
          </div>
        )}

        <div className="form-columns">
          {/* Columna Izquierda - Estadísticas */}
          <div className="form-column">
            {/* Estadísticas Generales */}
            <div className="form-section">
              <div className="section-header">
                <FiTrendingUp className="section-icon" />
                <h2 className="section-title">Estadísticas de Ventas</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Total de Ventas</label>
                <div className="form-value" style={{ fontSize: '24px', fontWeight: 'bold', color: '#667eea' }}>
                  {estadisticas?.total_sales || 0}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Cantidad Total Vendida</label>
                <div className="form-value" style={{ fontSize: '20px', fontWeight: '600', color: '#10b981' }}>
                  {estadisticas?.total_quantity_sold || 0} unidades
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Ingresos Totales</label>
                <div className="form-value" style={{ fontSize: '20px', fontWeight: '600', color: '#059669' }}>
                  {formatCurrency(estadisticas?.total_revenue || 0)}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Promedio por Venta</label>
                <div className="form-value">
                  {estadisticas?.average_quantity_per_sale?.toFixed(1) || '0.0'} unidades
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Precio Promedio</label>
                <div className="form-value">
                  {formatCurrency(estadisticas?.average_price || 0)}
                </div>
              </div>

              {estadisticas?.last_sale_date && (
                <div className="form-group">
                  <label className="form-label">Última Venta</label>
                  <div className="form-value">
                    <FiCalendar style={{ marginRight: '8px' }} />
                    {formatDate(estadisticas.last_sale_date)}
                  </div>
                </div>
              )}
            </div>

            {/* Información del Producto */}
            <div className="form-section">
              <div className="section-header">
                <FiShoppingCart className="section-icon" />
                <h2 className="section-title">Información del Producto</h2>
              </div>

              <div className="form-group">
                <label className="form-label">Código</label>
                <div className="form-value">{producto.code || 'N/A'}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Stock Actual</label>
                <div className="form-value">
                  {producto.current_stock || 0} unidades
                </div>
              </div>
            </div>

            {/* Paginación */}
            {pagination && (
              <div className="form-section">
                <div className="section-header">
                  <h2 className="section-title">Paginación</h2>
                </div>

                <div className="form-group">
                  <label className="form-label">Total de Registros</label>
                  <div className="form-value">{pagination.total || 0}</div>
                </div>

                <div className="form-group">
                  <label className="form-label">Página Actual</label>
                  <div className="form-value">
                    Página {pagination.page || 1} ({pagination.per_page || 50} por página)
                  </div>
                </div>

                {pagination.has_more && (
                  <div className="form-group">
                    <div className="form-value" style={{ color: '#667eea' }}>
                      Hay más registros disponibles
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Columna Derecha - Historial */}
          <div className="form-column">
            <div className="form-section">
              <div className="section-header">
                <FiDollarSign className="section-icon" />
                <h2 className="section-title">Historial de Ventas</h2>
              </div>

              {historialVentas.length === 0 ? (
                <div className="empty-state">
                  <FiShoppingCart size={48} style={{ color: '#cbd5e1', marginBottom: '16px' }} />
                  <h3>Sin Ventas Registradas</h3>
                  <p>Este producto aún no tiene ventas registradas en el sistema.</p>
                </div>
              ) : (
                <div className="sales-list" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {historialVentas.map((venta, index) => (
                    <div key={index} className="sale-card" style={{
                      padding: '16px',
                      marginBottom: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      transition: 'box-shadow 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                    onClick={() => venta.invoice_id && navigate(`/system/facturacion/${venta.invoice_id}`)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '16px', color: '#1f2937' }}>
                            {venta.invoice_number || `Venta #${venta.id}`}
                          </div>
                          <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                            {formatDateTime(venta.sale_date || venta.created_at)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>
                            {formatCurrency(venta.total_amount || (venta.quantity * venta.unit_price))}
                          </div>
                        </div>
                      </div>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '6px'
                      }}>
                        <div>
                          <div style={{ fontSize: '0.85em', color: '#6b7280', marginBottom: '4px' }}>Cantidad</div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            {venta.quantity || 0} unidades
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.85em', color: '#6b7280', marginBottom: '4px' }}>Precio Unitario</div>
                          <div style={{ fontWeight: '600', color: '#1f2937' }}>
                            {formatCurrency(venta.unit_price || 0)}
                          </div>
                        </div>
                      </div>

                      {venta.customer_name && (
                        <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#eff6ff', borderRadius: '4px' }}>
                          <div style={{ fontSize: '0.85em', color: '#1e40af' }}>
                            <strong>Cliente:</strong> {venta.customer_name}
                          </div>
                        </div>
                      )}

                      {venta.discount_amount > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#dc2626' }}>
                          <strong>Descuento:</strong> {formatCurrency(venta.discount_amount)}
                        </div>
                      )}

                      {venta.notes && (
                        <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6b7280' }}>
                          <strong>Notas:</strong> {venta.notes}
                        </div>
                      )}

                      {venta.status && (
                        <div style={{ marginTop: '8px' }}>
                          <span className={`status-badge status-badge--${venta.status.toLowerCase()}`}>
                            {venta.status}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Menú de navegación inferior */}
      <BottomTabMenu
        baseRoute={`/system/productos/${id}`}
        tabs={productTabs}
        currentTab="ventas"
      />
    </div>
  );
};

export default ProductoVentas;
